import { motion } from 'framer-motion';
import { Rocket, Star, Gift, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const earlyBenefits = [
  {
    icon: Gift,
    titleKey: 'landing.earlyAccess.b1title',
    descKey: 'landing.earlyAccess.b1desc',
    color: 'from-purple-500 to-pink-500',
    glow: 'rgba(168,85,247,0.2)',
  },
  {
    icon: Star,
    titleKey: 'landing.earlyAccess.b2title',
    descKey: 'landing.earlyAccess.b2desc',
    color: 'from-blue-500 to-cyan-500',
    glow: 'rgba(59,130,246,0.2)',
  },
  {
    icon: MessageCircle,
    titleKey: 'landing.earlyAccess.b3title',
    descKey: 'landing.earlyAccess.b3desc',
    color: 'from-emerald-500 to-teal-500',
    glow: 'rgba(16,185,129,0.2)',
  },
];

export function TestimonialsSection() {
  const { t } = useTranslation();
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 ">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-sm font-medium mb-6">
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
            >
              <Rocket className="w-4 h-4" />
            </motion.div>
            {t('landing.earlyAccess.badge')}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('landing.earlyAccess.headingPrefix')} <span className="text-primary-600 dark:text-primary-400">{t('landing.earlyAccess.headingHighlight')}</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('landing.earlyAccess.subtitle')}
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {earlyBenefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center"
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{
                  y: -6,
                  boxShadow: `0 24px 48px ${benefit.glow}`,
                  transition: { duration: 0.25 },
                }}
              >
                <motion.div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mx-auto mb-6`}
                  whileHover={{ scale: 1.15, rotate: 8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 12 }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {t(benefit.titleKey)}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {t(benefit.descKey)}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
